import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
  MenuItem,
  InputAdornment,
  Card,
  CardContent,
  Stack,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import API_BASE_URL from '../config/api';
import { toast } from 'react-toastify';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export default function EditExpenseDialog({ open, onClose, expense, onExpenseUpdated }) {
  const [formData, setFormData] = useState({
    Month: '',
    Year: '',
    Rent: '',
    Electricity: '',
    Snacks: '',
  });
  const [otherExpenses, setOtherExpenses] = useState([{ name: '', amount: '' }]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (expense) {
      setFormData({
        Month: expense.Month,
        Year: expense.Year,
        Rent: expense.Rent,
        Electricity: expense.Electricity,
        Snacks: expense.Snacks,
      });
      
      if (expense.OtherExpenses && expense.OtherExpenses.length > 0) {
        setOtherExpenses(expense.OtherExpenses);
      } else {
        setOtherExpenses([{ name: '', amount: '' }]);
      }
    }
  }, [expense]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOtherExpenseChange = (index, field, value) => {
    const updated = [...otherExpenses];
    updated[index][field] = value;
    setOtherExpenses(updated);
  };

  const addOtherExpense = () => {
    setOtherExpenses(prev => [...prev, { name: '', amount: '' }]);
  };

  const removeOtherExpense = (index) => {
    if (otherExpenses.length > 1) {
      setOtherExpenses(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.Month || !formData.Year) {
      toast.error('Please select month and year');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Filter out empty other expenses
      const validOtherExpenses = otherExpenses.filter(
        item => item.name.trim() && item.amount && parseFloat(item.amount) > 0
      );

      const payload = {
        ...formData,
        Month: parseInt(formData.Month),
        Year: parseInt(formData.Year),
        Rent: parseFloat(formData.Rent) || 0,
        Electricity: parseFloat(formData.Electricity) || 0,
        Snacks: parseFloat(formData.Snacks) || 0,
        OtherExpenses: validOtherExpenses,
      };

      const response = await fetch(`${API_BASE_URL}/api/expenses/${expense.ExpenseID}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        onExpenseUpdated();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to update expense');
      }
    } catch (error) {
      console.error('Error updating expense:', error);
      toast.error('Error updating expense');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    const rent = parseFloat(formData.Rent) || 0;
    const electricity = parseFloat(formData.Electricity) || 0;
    const snacks = parseFloat(formData.Snacks) || 0;
    const otherTotal = otherExpenses.reduce((sum, item) => {
      return sum + (parseFloat(item.amount) || 0);
    }, 0);
    
    return rent + electricity + snacks + otherTotal;
  };

  if (!expense) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: '#232323',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #C4A574',
        pb: 2,
        bgcolor: '#232323'
      }}>
        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 1, color: '#C4A574' }}>
          EDIT EXPENSES
        </Typography>
        <IconButton onClick={onClose} sx={{ color: '#C4A574' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ py: 3, bgcolor: '#232323' }}>
          <Grid container spacing={3}>
            {/* Month and Year */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Enter Month"
                value={formData.Month}
                onChange={(e) => handleInputChange('Month', e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#2e2e2e',
                    color: '#C4A574',
                    '& fieldset': { borderColor: '#C4A574' },
                    '&:hover fieldset': { borderColor: '#C4A574' },
                    '&.Mui-focused fieldset': { borderColor: '#C4A574' },
                  },
                  '& .MuiInputLabel-root': { color: '#C4A574' },
                  '& .MuiSelect-select': { color: '#C4A574' },
                }}
              >
                {MONTHS.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Enter Year"
                type="number"
                value={formData.Year}
                onChange={(e) => handleInputChange('Year', e.target.value)}
                required
                inputProps={{ min: 2020, max: 2100 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#2e2e2e',
                    color: '#C4A574',
                    '& fieldset': { borderColor: '#C4A574' },
                    '&:hover fieldset': { borderColor: '#C4A574' },
                    '&.Mui-focused fieldset': { borderColor: '#C4A574' },
                  },
                  '& .MuiInputLabel-root': { color: '#C4A574' },
                  '& .MuiInputBase-input': { color: '#C4A574' },
                }}
              />
            </Grid>

            {/* Fixed Expenses */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Rent"
                type="number"
                value={formData.Rent}
                onChange={(e) => handleInputChange('Rent', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#2e2e2e',
                    color: '#C4A574',
                    '& fieldset': { borderColor: '#C4A574' },
                    '&:hover fieldset': { borderColor: '#C4A574' },
                    '&.Mui-focused fieldset': { borderColor: '#C4A574' },
                  },
                  '& .MuiInputLabel-root': { color: '#C4A574' },
                  '& .MuiInputBase-input': { color: '#C4A574' },
                  '& .MuiInputAdornment-root': { color: '#C4A574' },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Electricity"
                type="number"
                value={formData.Electricity}
                onChange={(e) => handleInputChange('Electricity', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#2e2e2e',
                    color: '#C4A574',
                    '& fieldset': { borderColor: '#C4A574' },
                    '&:hover fieldset': { borderColor: '#C4A574' },
                    '&.Mui-focused fieldset': { borderColor: '#C4A574' },
                  },
                  '& .MuiInputLabel-root': { color: '#C4A574' },
                  '& .MuiInputBase-input': { color: '#C4A574' },
                  '& .MuiInputAdornment-root': { color: '#C4A574' },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Snacks"
                type="number"
                value={formData.Snacks}
                onChange={(e) => handleInputChange('Snacks', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#2e2e2e',
                    color: '#C4A574',
                    '& fieldset': { borderColor: '#C4A574' },
                    '&:hover fieldset': { borderColor: '#C4A574' },
                    '&.Mui-focused fieldset': { borderColor: '#C4A574' },
                  },
                  '& .MuiInputLabel-root': { color: '#C4A574' },
                  '& .MuiInputBase-input': { color: '#C4A574' },
                  '& .MuiInputAdornment-root': { color: '#C4A574' },
                }}
              />
            </Grid>

            {/* Other Expenses */}
            <Grid item xs={12}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: '#8B7355', fontWeight: 600 }}>
                      Other Expenses
                    </Typography>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={addOtherExpense}
                      variant="outlined"
                      size="small"
                      sx={{ color: '#8B7355', borderColor: '#C4A574' }}
                    >
                      Add
                    </Button>
                  </Box>

                  <Stack spacing={2}>
                    {otherExpenses.map((expense, index) => (
                      <Paper key={index} variant="outlined" sx={{ p: 1.25 }}>
                        <Grid container spacing={1} alignItems="center">
                          <Grid item xs={12} sm={7}>
                            <TextField
                              fullWidth
                              label="Expense Name"
                              value={expense.name}
                              onChange={(e) => handleOtherExpenseChange(index, 'name', e.target.value)}
                              size="small"
                              placeholder="e.g., TV, Furniture"
                            />
                          </Grid>

                          <Grid item xs={12} sm={otherExpenses.length > 1 ? 3 : 5}>
                            <TextField
                              fullWidth
                              label="Amount"
                              type="number"
                              value={expense.amount}
                              onChange={(e) => handleOtherExpenseChange(index, 'amount', e.target.value)}
                              size="small"
                              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                            />
                          </Grid>

                          {otherExpenses.length > 1 && (
                            <Grid item xs={12} sm={2}>
                              <Button
                                onClick={() => removeOtherExpense(index)}
                                variant="outlined"
                                color="error"
                                size="small"
                                fullWidth
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                              >
                                Remove
                              </Button>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Total */}
            <Grid item xs={12}>
              <Box sx={{ 
                p: 2, 
                backgroundColor: 'rgba(196, 165, 116, 0.12)', 
                borderRadius: 1,
                border: '1px solid #C4A574'
              }}>
                <Typography variant="h6" sx={{ color: '#C4A574', fontWeight: 700 }}>
                  Total Amount: ₹{calculateTotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #C4A574', bgcolor: '#232323' }}>
          <Button 
            onClick={onClose}
            sx={{ 
              color: '#C4A574',
              borderColor: '#C4A574',
              '&:hover': { borderColor: '#C4A574' }
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              bgcolor: '#C4A574',
              color: 'white',
              fontWeight: 600,
              px: 4,
              '&:hover': { bgcolor: '#B8941F' },
              '&:disabled': { bgcolor: '#666', color: '#999' },
            }}
          >
            {loading ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}